import { useState } from 'react';
import { motion as Motion } from 'motion/react';
import { Mail, Calendar, KeyRound, Copy, Check, Clock, RefreshCw, Plus, AlertTriangle, ShieldAlert, ArrowRight, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { apiClient, apiPath } from '@/lib/api-client';
import { colors, accent, FADE } from '@/lib/tokens';
import { isAllowedAvatarUrl } from '@/lib/avatar-url';
import { Spinner } from '@/components/ui/spinner';
import { PlanUsageSection } from '@/components/PlanUsageSection';
import BugReportForm from '@/components/BugReportForm';
import { SignOutButton } from '@/components/dashboard/SignOutButton';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at?: string | null;
  created_at: string;
}

interface CreatedKey {
  id: string;
  key: string;
  name: string;
  key_prefix: string;
  created_at: string;
}

function fmtShort(dateStr?: string | null) {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtLong(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied — do nothing, button stays in default state
    }
  }
  return (
    <button
      onClick={copy}
      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-all cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
      style={{
        background: copied ? colors.accentSubtle : colors.bgSubtle,
        border: `1px solid ${copied ? colors.accentBorder : colors.borderDefault}`,
        color: copied ? accent : colors.textMuted,
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export default function Account() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [revealedKey, setRevealedKey] = useState<CreatedKey | null>(null);
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotateError, setRotateError] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [generateError, setGenerateError] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  function openReveal(key: CreatedKey) {
    setRevealedKey(key);
    setShowRevealModal(true);
  }

  function dismissReveal() {
    setShowRevealModal(false);
    setRevealedKey(null);
  }

  const KEY_NAME_LIMIT = 64;

  const { data: keys = [], isLoading: keysLoading, isError: keysError, refetch: refetchKeys } = useQuery<ApiKey[]>({
    queryKey: ['dashboard-keys'],
    queryFn: () => apiClient.get<{ data: ApiKey[] }>('/auth/keys').then((r) => r.data),
  });

  const activeKey = keys[0] ?? null;

  const generateMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<{ data: CreatedKey }>('/auth/keys', { name }).then((r) => r.data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['dashboard-keys'] });
      setShowGenerate(false);
      setKeyName('');
      setGenerateError('');
      openReveal(created);
    },
    onError: (e: Error) => setGenerateError(e.message),
  });

  const rotateMutation = useMutation({
    mutationFn: async () => {
      const fresh = await apiClient.get<{ data: ApiKey[] }>('/auth/keys');
      const existingName = fresh.data[0]?.name ?? 'default';
      for (const k of fresh.data) {
        try { await apiClient.del(apiPath`/auth/keys/${k.id}`); } catch { /* already deleted */ }
      }
      const res = await apiClient.post<{ data: CreatedKey }>('/auth/keys', { name: existingName });
      return res.data;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['dashboard-keys'] });
      setConfirmRotate(false);
      setRotateError('');
      openReveal(created);
    },
    onError: (e: Error) => setRotateError(e.message),
  });

  if (!user) return null;

  return (
    <div className="min-h-full">
      <div className="w-full px-6 md:px-10 pt-8 pb-16">

        {/* Header */}
        <Motion.div {...FADE(0)} className="mb-8">
          <p className="text-xs uppercase tracking-[0.12em] mb-2" style={{ color: accent }}>Settings</p>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: colors.textPrimary }}>Account & API Key</h1>
        </Motion.div>

        {/* Combined card */}
        <Motion.div
          {...FADE(0.08)}
          className="rounded-lg overflow-hidden"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* LEFT — Profile */}
            <div className="flex flex-col border-b lg:border-b-0 lg:border-r" style={{ borderColor: colors.borderSubtle }}>

              {/* Avatar + name */}
              <div className="flex items-start gap-4 px-5 py-5 sm:px-7 sm:py-7" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                <div className="relative flex-shrink-0">
                  {isAllowedAvatarUrl(user.avatarUrl) ? (
                    <img
                      src={user.avatarUrl!}
                      alt={user.name ?? ''}
                      className="w-14 h-14 rounded-md"
                      loading="lazy"
                      decoding="async"
                      style={{ outline: `1px solid ${colors.borderDefault}` }}
                    />
                  ) : (
                    <div
                      className="w-14 h-14 rounded-md flex items-center justify-center text-lg font-bold"
                      style={{ background: colors.accentSubtle, color: accent }}
                    >
                      {(user.name ?? user.email)[0].toUpperCase()}
                    </div>
                  )}
                  <div
                    className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-sm border-2 flex items-center justify-center"
                    style={{ background: accent, borderColor: colors.bgBase }}
                  >
                    <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold truncate" style={{ color: colors.textPrimary }}>{user.name ?? user.email}</h2>
                  <p className="text-xs mt-1 font-mono truncate" style={{ color: colors.textMuted }}>{user.email}</p>
                </div>
              </div>

              {/* Email row */}
              <div className="flex items-center gap-4 px-5 py-4 sm:px-7" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.textMuted }} />
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] mb-0.5" style={{ color: colors.textMuted }}>Email</p>
                  <p className="text-sm" style={{ color: colors.textPrimary }}>{user.email}</p>
                </div>
              </div>

              {/* Member since row */}
              <div className="flex items-center gap-4 px-5 py-4 sm:px-7" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.textMuted }} />
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] mb-0.5" style={{ color: colors.textMuted }}>Member since</p>
                  <p className="text-sm" style={{ color: colors.textPrimary }}>{fmtLong(user.createdAt)}</p>
                </div>
              </div>

              {/* User ID + sign out */}
              <div className="flex-1 flex flex-col justify-end">
                <div className="px-5 py-4 sm:px-7" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                  <div className="flex items-center justify-between w-full">
                    <p className="text-xs uppercase tracking-[0.1em]" style={{ color: colors.textMuted }}>User ID</p>
                    <code className="text-xs font-mono" style={{ color: colors.textMuted }}>{user.id}</code>
                  </div>
                </div>
                <div className="px-5 py-4 sm:px-7">
                  <SignOutButton variant="destructive" />
                </div>
              </div>
            </div>

            {/* RIGHT — API Key */}
            <div className="flex flex-col">
              {keysLoading ? (
                <div className="flex-1 flex items-center justify-center py-16">
                  <Spinner />
                </div>
              ) : keysError ? (
                <div className="flex-1 flex flex-col items-center justify-center py-16 text-center px-6">
                  <AlertTriangle className="w-5 h-5 mb-3" style={{ color: colors.error }} />
                  <p className="text-sm font-semibold mb-1" style={{ color: colors.textPrimary }}>Failed to load key</p>
                  <p className="text-xs mb-4" style={{ color: colors.textMuted }}>Check your connection and try again.</p>
                  <button
                    onClick={() => refetchKeys()}
                    className="flex items-center gap-2 px-4 py-2 rounded-md text-sm cursor-pointer"
                    style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}`, color: colors.error }}
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                  </button>
                </div>
              ) : activeKey ? (
                <>
                  {/* Key header */}
                  <div className="flex items-center gap-4 px-5 py-4 sm:px-6 sm:py-5" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                    <KeyRound className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: colors.textPrimary }}>{activeKey.name || 'API Key'}</p>
                      <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Key hidden. Rotate to generate a new one.</p>
                    </div>
                  </div>

                  {/* Masked key */}
                  <div className="px-5 py-4 sm:px-6" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
                    <div className="flex items-center px-4 py-2.5 rounded-md" style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderSubtle}` }}>
                      <code className="text-xs font-mono flex-1">
                        <span style={{ color: accent }}>{activeKey.key_prefix}</span>
                        <span style={{ color: colors.textMuted }}>••••••••••••••••••••</span>
                      </code>
                    </div>
                  </div>

                  {/* Created / last used */}
                  <div className="grid grid-cols-2 divide-x" style={{ borderBottom: `1px solid ${colors.borderSubtle}`, borderColor: colors.borderSubtle }}>
                    <div className="px-4 py-4 sm:px-6">
                      <p className="text-xs uppercase tracking-[0.1em] mb-1" style={{ color: colors.textMuted }}>Created</p>
                      <p className="text-sm" style={{ color: colors.textSecondary }}>{fmtShort(activeKey.created_at)}</p>
                    </div>
                    <div className="px-4 py-4 sm:px-6">
                      <p className="text-xs uppercase tracking-[0.1em] mb-1" style={{ color: colors.textMuted }}>Last used</p>
                      <p className="flex items-center gap-1.5 text-sm" style={{ color: colors.textSecondary }}>
                        <Clock className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
                        {fmtShort(activeKey.last_used_at)}
                      </p>
                    </div>
                  </div>

                  {/* Rotate action */}
                  <div className="flex-1 flex items-end">
                    <div className="flex items-center gap-3 px-6 py-5 w-full">
                      <button
                        onClick={() => setConfirmRotate(true)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer"
                        style={{ background: colors.warningSubtle, border: `1px solid ${colors.warningBorder}`, color: colors.warning }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(251,191,36,0.12)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.warningSubtle; }}
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Rotate Key
                      </button>
                      <p className="text-xs" style={{ color: colors.textMuted }}>Invalidates the current key immediately.</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-center px-6">
                  <KeyRound className="w-7 h-7 mb-4" style={{ color: colors.textMuted }} />
                  <p className="text-[15px] font-semibold mb-1" style={{ color: colors.textPrimary }}>No API key yet</p>
                  <p className="text-sm mb-6" style={{ color: colors.textMuted }}>Generate a key to start authenticating requests</p>
                  <button
                    onClick={() => { setShowGenerate(true); setGenerateError(''); }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer"
                    style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                  >
                    <Plus className="w-4 h-4" /> Generate API Key
                  </button>
                </div>
              )}
            </div>
          </div>
        </Motion.div>

        {/* Plan usage */}
        <Motion.div {...FADE(0.11)} className="mt-4">
          <PlanUsageSection />
        </Motion.div>

        {/* Help section */}
        <Motion.div
          {...FADE(0.17)}
          className="mt-4 rounded-lg overflow-hidden"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* Legal */}
            <div className="flex flex-col border-b lg:border-b-0 lg:border-r px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
              <p className="text-xs uppercase tracking-[0.1em] mb-0.5" style={{ color: colors.textMuted }}>Legal</p>
              <p className="text-sm mt-2 mb-4" style={{ color: colors.textSecondary }}>Review our terms and privacy policy.</p>
              <div className="flex items-center gap-3">
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm transition-colors"
                  style={{ color: colors.textMuted, textDecoration: 'none' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                >
                  Terms of Service <ArrowRight className="w-3.5 h-3.5" />
                </a>
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm transition-colors"
                  style={{ color: colors.textMuted, textDecoration: 'none' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                >
                  Privacy Policy <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Feedback */}
            <div className="flex flex-col px-5 py-4 sm:px-7">
              <p className="text-xs uppercase tracking-[0.1em] mb-0.5" style={{ color: colors.textMuted }}>Feedback</p>
              <p className="text-sm mt-2 mb-4" style={{ color: colors.textSecondary }}>Found something broken or have a suggestion? Let us know.</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFeedback(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer"
                  style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                >
                  Send Feedback <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <Link
                  to="/docs"
                  className="inline-flex items-center gap-1.5 text-sm transition-colors"
                  style={{ color: colors.textMuted, textDecoration: 'none' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textMuted; }}
                >
                  View docs <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

          </div>
        </Motion.div>

      </div>

      {/* One-time key reveal modal */}
      <Dialog
        open={showRevealModal && !!revealedKey}
        onOpenChange={(open) => { if (!open) dismissReveal(); }}
      >
        <DialogContent
          aria-labelledby="reveal-dialog-title"
          showCloseButton={false}
          className="w-full max-w-sm rounded-lg p-6 shadow-2xl"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
        >
              <div className="flex items-start gap-3 mb-5">
                <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accent }} />
                <div>
                  <h2 id="reveal-dialog-title" className="text-[15px] font-bold mb-1" style={{ color: colors.textPrimary }}>Your API Key</h2>
                  <p className="text-sm font-mono truncate" style={{ color: colors.textMuted }}>{revealedKey?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 px-4 py-2.5 rounded-md min-w-0" style={{ background: colors.bgSubtle, border: `1px solid ${colors.accentBorder}` }}>
                  <code className="block text-xs truncate font-mono" style={{ color: accent }}>{revealedKey?.key}</code>
                </div>
                <CopyButton value={revealedKey?.key ?? ''} />
              </div>
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-md mb-5" style={{ background: colors.warningSubtle, border: `1px solid ${colors.warningBorder}` }}>
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.warning }} />
                <p className="text-xs leading-relaxed" style={{ color: colors.warning }}>
                  This key will <strong>not be shown again</strong>. Copy it now and store it somewhere safe.
                </p>
              </div>
              <button
                onClick={dismissReveal}
                className="w-full px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer"
                style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
              >
                I've saved my key
              </button>
        </DialogContent>
      </Dialog>

      {/* Generate key modal */}
      <Dialog
        open={showGenerate}
        onOpenChange={(open) => { if (!open) { setShowGenerate(false); setKeyName(''); setGenerateError(''); } }}
      >
        <DialogContent
          aria-labelledby="generate-dialog-title"
          showCloseButton={false}
          className="w-full max-w-sm rounded-lg p-6 shadow-2xl"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
          onOpenAutoFocus={(e) => { e.preventDefault(); }}
        >
              <div className="flex items-start gap-3 mb-5">
                <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accent }} />
                <div>
                  <h2 id="generate-dialog-title" className="text-[15px] font-bold mb-1" style={{ color: colors.textPrimary }}>Generate API Key</h2>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Give your key a name so you can identify it later.</p>
                </div>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (keyName.trim()) generateMutation.mutate(keyName.trim()); }}>
                <input
                  autoFocus
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value.slice(0, KEY_NAME_LIMIT))}
                  placeholder="e.g. production"
                  maxLength={KEY_NAME_LIMIT}
                  className="w-full px-4 py-2.5 rounded-md text-sm placeholder:text-[#a8a29e] focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] transition-all"
                  style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textPrimary }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = colors.accentBorder; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = colors.borderDefault; }}
                />
                <div className="flex items-center justify-between mt-1.5 mb-1">
                  {generateError
                    ? <p className="text-xs" style={{ color: colors.error }}>{generateError}</p>
                    : <span />
                  }
                  <p className="text-xs tabular-nums" style={{ color: keyName.length >= KEY_NAME_LIMIT ? colors.error : colors.textMuted }}>
                    {keyName.length}/{KEY_NAME_LIMIT}
                  </p>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => { setShowGenerate(false); setKeyName(''); setGenerateError(''); }}
                    className="flex-1 px-4 py-2.5 rounded-md text-sm transition-all cursor-pointer"
                    style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textMuted }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!keyName.trim() || generateMutation.isPending}
                    className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                  >
                    {generateMutation.isPending ? 'Generating…' : 'Generate'}
                  </button>
                </div>
              </form>
        </DialogContent>
      </Dialog>

      {/* Rotate confirmation modal */}
      <Dialog
        open={confirmRotate}
        onOpenChange={(open) => { if (!open) { setConfirmRotate(false); setRotateError(''); } }}
      >
        <DialogContent
          aria-labelledby="rotate-dialog-title"
          showCloseButton={false}
          className="w-full max-w-sm rounded-lg p-6 shadow-2xl"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
        >
              <div className="flex items-start gap-3 mb-5">
                <RefreshCw className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.warning }} />
                <div>
                  <h2 id="rotate-dialog-title" className="text-[15px] font-bold mb-1" style={{ color: colors.textPrimary }}>Rotate API key?</h2>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Your current key will be immediately invalidated.</p>
                </div>
              </div>
              {rotateError && <p className="text-xs mb-4" style={{ color: colors.error }}>{rotateError}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => { setConfirmRotate(false); setRotateError(''); }}
                  className="flex-1 px-4 py-2.5 rounded-md text-sm transition-all cursor-pointer"
                  style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textMuted }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => rotateMutation.mutate()}
                  disabled={rotateMutation.isPending}
                  className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer disabled:opacity-40"
                  style={{ background: colors.warningSubtle, border: `1px solid ${colors.warningBorder}`, color: colors.warning }}
                >
                  {rotateMutation.isPending ? 'Rotating…' : 'Rotate Key'}
                </button>
              </div>
        </DialogContent>
      </Dialog>

      {/* Feedback modal */}
      <Dialog
        open={showFeedback}
        onOpenChange={(open) => { if (!open) setShowFeedback(false); }}
      >
        <DialogContent
          aria-labelledby="feedback-dialog-title"
          showCloseButton={false}
          className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-lg p-6 shadow-2xl"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
        >
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <h2 id="feedback-dialog-title" className="text-[15px] font-bold mb-1" style={{ color: colors.textPrimary }}>Send Feedback</h2>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Found something broken or have a suggestion? Let us know.</p>
                </div>
                <button
                  onClick={() => setShowFeedback(false)}
                  aria-label="Close"
                  className="flex-shrink-0 p-1 rounded-md transition-colors cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
                  style={{ color: colors.textMuted }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted; }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <BugReportForm compact defaultArea="dashboard" onSuccess={() => setTimeout(() => setShowFeedback(false), 1500)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
